import { createContext, useContext } from 'react';
import type { BrandResponse } from './brand';

/**
 * Root-level tenant branding for the customer portal.
 *
 * `CustomerBrandProvider` (mounted once under `CustomerLocaleProvider`) fetches
 * `GET /api/customer/branding` a single time per page load and publishes the
 * response here. Consumers that need the tenant name / primary colour
 * (`CustomerPortalHeader`, `BrandedShell`) read this instead of issuing their
 * own fetch. `loaded` flips true once the request settles — success OR
 * failure — so a consumer can distinguish "still loading" from "tenant has no
 * branding / not resolved".
 *
 * Null context (no provider mounted — e.g. a page rendered standalone in a
 * test) means "no root fetch happened"; `BrandedShell` falls back to its own
 * fetch in that case.
 */
export interface CustomerBrandState {
  brand: BrandResponse | null;
  loaded: boolean;
}

export const CustomerBrandContext = createContext<CustomerBrandState | null>(null);

export function useCustomerBrand(): CustomerBrandState {
  return useContext(CustomerBrandContext) ?? { brand: null, loaded: false };
}
